// A shared tone curve lifts dark frames and recovers contrast in washed-out
// frames without changing geometry or the camera preview. Pixels stay local.
export function normalizeTrackingPixels(pixels: Uint8ClampedArray, width?: number, height?: number) {
  const histogram = new Uint32Array(256);
  const count = pixels.length / 4;
  if (!count) return;
  for (let i = 0; i < pixels.length; i += 4) {
    const luminance = (77 * pixels[i] + 150 * pixels[i + 1] + 29 * pixels[i + 2]) >> 8;
    histogram[luminance]++;
  }
  let low = 0;
  let high = 255;
  let total = 0;
  for (let value = 0; value < 256; value++) {
    total += histogram[value];
    if (total >= count * 0.02) { low = value; break; }
  }
  total = 0;
  for (let value = 255; value >= 0; value--) {
    total += histogram[value];
    if (total >= count * 0.02) { high = value; break; }
  }
  // A clipped/flat frame contains no recoverable face detail. Do not magnify
  // quantization noise into features or invent a successful detection.
  if (high - low < 8) return;
  const gain = Math.min(12, 180 / (high - low));
  const offset = 128 - (low + high) * gain / 2;
  const curve = new Uint8ClampedArray(256);
  for (let value = 0; value < 256; value++) curve[value] = value * gain + offset;
  for (let i = 0; i < pixels.length; i += 4) {
    pixels[i] = curve[pixels[i]];
    pixels[i + 1] = curve[pixels[i + 1]];
    pixels[i + 2] = curve[pixels[i + 2]];
  }
  if (width && height) recoverLocalContrast(pixels, width, height);
}

function recoverLocalContrast(pixels: Uint8ClampedArray, width: number, height: number) {
  const columns = 8;
  const rows = 8;
  const tileWidth = width / columns;
  const tileHeight = height / rows;
  const curves: Uint8ClampedArray[] = [];
  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      const histogram = new Uint32Array(256);
      let count = 0;
      for (let y = Math.floor(row * tileHeight); y < Math.floor((row + 1) * tileHeight); y++) {
        for (let x = Math.floor(column * tileWidth); x < Math.floor((column + 1) * tileWidth); x++) {
          const i = (y * width + x) * 4;
          histogram[(77 * pixels[i] + 150 * pixels[i + 1] + 29 * pixels[i + 2]) >> 8]++;
          count++;
        }
      }
      let low = 0;
      let high = 255;
      let total = 0;
      for (let v = 0; v < 256; v++) { total += histogram[v]; if (total >= count * .02) { low = v; break; } }
      total = 0;
      for (let v = 255; v >= 0; v--) { total += histogram[v]; if (total >= count * .02) { high = v; break; } }
      const range = high - low;
      const needsCorrection = range >= 2 && range < 100 && (low > 180 || high < 75);
      const gain = needsCorrection ? Math.min(12, 180 / range) : 1;
      const offset = needsCorrection ? 128 - (low + high) * gain / 2 : 0;
      const curve = new Uint8ClampedArray(256);
      for (let v = 0; v < 256; v++) curve[v] = v * gain + offset;
      curves.push(curve);
    }
  }
  // Interpolate between tile centres so illumination corrections have no seams.
  for (let y = 0; y < height; y++) {
    const gy = Math.max(0, Math.min(rows - 1, (y + .5) / tileHeight - .5));
    const top = Math.floor(gy), bottom = Math.min(rows - 1, top + 1), fy = gy - top;
    for (let x = 0; x < width; x++) {
      const gx = Math.max(0, Math.min(columns - 1, (x + .5) / tileWidth - .5));
      const left = Math.floor(gx), right = Math.min(columns - 1, left + 1), fx = gx - left;
      const a = curves[top * columns + left], b = curves[top * columns + right];
      const c = curves[bottom * columns + left], d = curves[bottom * columns + right];
      const i = (y * width + x) * 4;
      for (let channel = 0; channel < 3; channel++) {
        const v = pixels[i + channel];
        pixels[i + channel] = (a[v] * (1 - fx) + b[v] * fx) * (1 - fy) + (c[v] * (1 - fx) + d[v] * fx) * fy;
      }
    }
  }
}

export class TrackingFrameNormalizer {
  private canvas: HTMLCanvasElement | null = null;

  prepare(video: HTMLVideoElement): HTMLVideoElement | HTMLCanvasElement {
    this.canvas ??= document.createElement("canvas");
    const scale = Math.min(1, 480 / Math.max(video.videoWidth, video.videoHeight));
    const width = Math.max(1, Math.round(video.videoWidth * scale));
    const height = Math.max(1, Math.round(video.videoHeight * scale));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
    const context = this.canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return video;
    context.drawImage(video, 0, 0, width, height);
    const frame = context.getImageData(0, 0, width, height);
    normalizeTrackingPixels(frame.data, width, height);
    context.putImageData(frame, 0, 0);
    return this.canvas;
  }

  reset() {
    if (this.canvas) { this.canvas.width = 1; this.canvas.height = 1; }
    this.canvas = null;
  }
}
